import React, { useState, useEffect, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { 
  Container, 
  Box, 
  Typography, 
  Button, 
  AppBar, 
  Toolbar, 
  Paper,
  Card,
  CardContent,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress
} from '@mui/material';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import PartnerRegistration from './components/registration/PartnerRegistration';
import PartnerAdmin from './components/admin/PartnerAdmin';
// Bỏ import EmailConfigAdmin vì đang có lỗi
import EmailTest from './components/test/EmailTest';
import PartnerChatbot from './components/chatbot/PartnerChatbot';
import { AdminPanelSettings, AppRegistration, Home, Email, Settings, Login, Logout } from '@mui/icons-material';
import './App.css';
import { auth } from './config/firebase';
import { signInWithEmailAndPassword, signOut, User } from 'firebase/auth';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';

// Khởi tạo Firebase Functions và kết nối với Emulator trong môi trường dev nếu cần
const functions = getFunctions();
// Nếu bạn đang chạy emulator, uncomment dòng dưới đây
// if (process.env.NODE_ENV === 'development') {
//   connectFunctionsEmulator(functions, 'localhost', 5001);
// }

// Component tạm thời thay thế cho EmailConfigAdmin
const TemporaryEmailConfigAdmin = () => {
  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>
          Cấu hình Email
        </Typography>
        <Typography variant="body1">
          Tính năng này đang được cập nhật. Vui lòng quay lại sau.
        </Typography>
      </Paper>
    </Container>
  );
};

// Login Dialog Component with proper TypeScript types
interface LoginDialogProps {
  open: boolean;
  onClose: () => void;
  onLogin: () => void;
}

const LoginDialog: React.FC<LoginDialogProps> = ({ open, onClose, onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async () => {
    if (!email || !password) {
      setError('Vui lòng nhập email và mật khẩu');
      return;
    }

    try {
      setLoading(true);
      setError('');
      await signInWithEmailAndPassword(auth, email, password);
      onLogin();
      onClose();
      toast.success('Đăng nhập thành công');
    } catch (err) {
      console.error('Đăng nhập thất bại:', err);
      setError('Email hoặc mật khẩu không đúng');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Đăng nhập Admin</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Email"
          type="email"
          fullWidth
          variant="outlined"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <TextField
          margin="dense"
          label="Mật khẩu"
          type="password"
          fullWidth
          variant="outlined"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        {error && (
          <Typography color="error" variant="body2" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Hủy</Button>
        <Button 
          onClick={handleLogin} 
          variant="contained" 
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <Login />}
        >
          Đăng nhập
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const HomePage = () => {
  return (
    <Container maxWidth="lg">
      <Box sx={{ 
        my: { xs: 3, md: 6 }, 
        textAlign: 'center',
        px: { xs: 2, md: 4 }
      }}>
        <Typography 
          variant="h3" 
          component="h1" 
          gutterBottom 
          fontWeight="bold"
          sx={{ 
            fontSize: { xs: '1.8rem', sm: '2.3rem', md: '3rem' },
            mb: 2,
            animation: 'fadeIn 1s ease-out',
            '@keyframes fadeIn': {
              '0%': { opacity: 0, transform: 'translateY(-20px)' },
              '100%': { opacity: 1, transform: 'translateY(0)' }
            }
          }}
        >
          Bookdi Platform
        </Typography>
        
        <Typography 
          variant="h6" 
          color="text.secondary" 
          paragraph
          sx={{ 
            fontSize: { xs: '0.9rem', md: '1.25rem' },
            mb: { xs: 2, md: 3 },
            maxWidth: '800px',
            mx: 'auto',
            animation: 'fadeIn 1s ease-out 0.3s',
            animationFillMode: 'both',
            '@keyframes fadeIn': {
              '0%': { opacity: 0, transform: 'translateY(-10px)' },
              '100%': { opacity: 1, transform: 'translateY(0)' }
            }
          }}
        >
          Nền tảng đặt lịch dịch vụ hàng đầu Việt Nam - Kết nối doanh nghiệp với khách hàng một cách hiệu quả
        </Typography>

        {/* Hero Banner */}
        <Box
          sx={{
            mt: 2,
            mb: { xs: 4, md: 6 },
            mx: 'auto',
            position: 'relative',
            borderRadius: { xs: 2, md: 4 },
            overflow: 'hidden',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
            height: { xs: '220px', sm: '300px', md: '400px' },
            background: 'linear-gradient(45deg, #f06292, #f8bbd0)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: 'fadeIn 1s ease-out 0.5s',
            animationFillMode: 'both',
            '@keyframes fadeIn': {
              '0%': { opacity: 0, transform: 'scale(0.98)' },
              '100%': { opacity: 1, transform: 'scale(1)' }
            }
          }}
        >
          <Box sx={{ textAlign: 'center', maxWidth: '90%', p: { xs: 1.5, md: 2 } }}>
            <Typography
              variant="h4"
              sx={{
                color: 'white',
                textShadow: '0 2px 4px rgba(0,0,0,0.2)',
                fontWeight: 'bold',
                fontSize: { xs: '1.2rem', sm: '1.5rem', md: '2rem' },
                px: { xs: 1, md: 3 },
                textAlign: 'center',
                mb: { xs: 2, md: 4 }
              }}
            >
              Nền tảng đặt lịch thông minh cho mọi loại hình dịch vụ
            </Typography>
            
            <Button
              component={Link}
              to="/register"
              variant="contained"
              size="large"
              sx={{
                bgcolor: 'white',
                color: '#e91e63',
                fontWeight: 'bold',
                px: { xs: 3, md: 4 },
                py: { xs: 1, md: 1.5 },
                fontSize: { xs: '0.85rem', md: '1rem' },
                borderRadius: 2,
                textTransform: 'none',
                boxShadow: '0 4px 10px rgba(0,0,0,0.2)',
                transition: 'all 0.3s ease',
                border: '2px solid white',
                '&:hover': {
                  bgcolor: 'transparent',
                  color: 'white',
                  transform: 'translateY(-3px)',
                  boxShadow: '0 6px 15px rgba(0,0,0,0.3)',
                },
                animation: 'pulse 2s infinite',
                '@keyframes pulse': {
                  '0%': { transform: 'scale(1)' },
                  '50%': { transform: 'scale(1.05)' },
                  '100%': { transform: 'scale(1)' }
                }
              }}
            >
              Đăng Ký Ngay
            </Button>
          </Box>
        </Box>

        {/* Benefits Section - Enhanced */}
        <Box 
          sx={{ 
            mb: { xs: 5, md: 8 },
            animation: 'fadeIn 1s ease-out 0.7s',
            animationFillMode: 'both',
            '@keyframes fadeIn': {
              '0%': { opacity: 0 },
              '100%': { opacity: 1 }
            }
          }}
        >
          <Typography 
            variant="h4" 
            component="h2" 
            gutterBottom 
            sx={{ 
              mb: { xs: 3, md: 5 },
              fontSize: { xs: '1.4rem', md: '2rem' },
              fontWeight: 'bold',
              position: 'relative',
              display: 'inline-block',
              '&:after': {
                content: '""',
                position: 'absolute',
                width: '60px',
                height: '4px',
                background: 'linear-gradient(to right, #e91e63, #f8bbd0)',
                bottom: '-12px',
                left: 'calc(50% - 30px)',
                borderRadius: '2px'
              }
            }}
          >
            Lợi ích khi trở thành đối tác Bookdi
          </Typography>

          {/* Benefit 1 - Ecosystem */}
          <Paper 
            elevation={2} 
            sx={{ 
              p: { xs: 2, md: 4 }, 
              mb: 3, 
              borderRadius: 3,
              background: 'linear-gradient(to right, #fce4ec, #ffffff)',
              border: '1px solid #f8bbd0',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: '0 12px 30px rgba(0, 0, 0, 0.08)',
              }
            }}
          >
            <Box 
              sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { xs: 'flex-start', md: 'center' },
                textAlign: { xs: 'left', md: 'left' },
                gap: 2
              }}
            >
              <Box sx={{ minWidth: { md: '200px' } }}>
                <Typography 
                  variant="h5" 
                  component="h3" 
                  color="primary" 
                  sx={{ 
                    fontWeight: 'bold',
                    mb: { xs: 1, md: 0 },
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: { xs: '1.1rem', md: '1.25rem' }
                  }}
                >
                  <Box 
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '45px',
                      height: '45px',
                      borderRadius: '50%',
                      background: 'linear-gradient(45deg, #c2185b, #f06292)',
                      color: 'white',
                      marginRight: '15px',
                      boxShadow: '0 4px 10px rgba(233, 30, 99, 0.3)',
                      flexShrink: 0
                    }}
                  >
                    <span role="img" aria-label="network" style={{ fontSize: '22px' }}>🌐</span>
                  </Box>
                  <span>Hệ sinh thái truyền thông</span>
                </Typography>
              </Box>
              <Box>
                <Typography variant="body1" paragraph sx={{ mb: 1, fontSize: { xs: '0.9rem', md: '1rem' } }}>
                  <strong>Fanpage Địa Điểm Ninh Thuận</strong> (135.000+ người theo dõi)
                </Typography>
                <Typography variant="body1" paragraph sx={{ mb: 1, fontSize: { xs: '0.9rem', md: '1rem' } }}>
                  <strong>Group Review Phan Rang Ninh Thuận</strong> (140.000+ thành viên hoạt động)
                </Typography>
                <Typography variant="body1" paragraph sx={{ mb: 1, fontSize: { xs: '0.9rem', md: '1rem' } }}>
                  <strong>Kênh TikTok – Ăn Gì Ở Phan Rang</strong>
                </Typography>
                <Typography variant="body1" paragraph sx={{ mb: 0, fontSize: { xs: '0.9rem', md: '1rem' } }}>
                  <strong>App liên kết Ninh Thuận GO</strong> – Giao đồ ăn, giới thiệu địa điểm nổi bật
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* Benefit 2 - Credibility */}
          <Paper 
            elevation={2} 
            sx={{ 
              p: { xs: 2, md: 4 }, 
              mb: 3, 
              borderRadius: 3,
              background: 'linear-gradient(to right, #f8bbd0, #ffffff)',
              border: '1px solid #f48fb1',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: '0 12px 30px rgba(0, 0, 0, 0.08)',
              }
            }}
          >
            <Box 
              sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { xs: 'flex-start', md: 'center' },
                textAlign: { xs: 'left', md: 'left' },
                gap: 2
              }}
            >
              <Box sx={{ minWidth: { md: '200px' } }}>
                <Typography 
                  variant="h5" 
                  component="h3" 
                  color="primary" 
                  sx={{ 
                    fontWeight: 'bold',
                    mb: { xs: 1, md: 0 },
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: { xs: '1.1rem', md: '1.25rem' }
                  }}
                >
                  <Box 
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '45px',
                      height: '45px',
                      borderRadius: '50%',
                      background: 'linear-gradient(45deg, #ad1457, #ec407a)',
                      color: 'white',
                      marginRight: '15px',
                      boxShadow: '0 4px 10px rgba(233, 30, 99, 0.3)',
                      flexShrink: 0
                    }}
                  >
                    <span role="img" aria-label="credibility" style={{ fontSize: '22px' }}>🏆</span>
                  </Box>
                  <span>Tăng độ uy tín</span>
                </Typography>
              </Box>
              <Box>
                <Typography variant="body1" paragraph sx={{ mb: 1, fontSize: { xs: '0.9rem', md: '1rem' } }}>
                  Hỗ trợ thiết kế hình ảnh bài đăng, video review cơ bản
                </Typography>
                <Typography variant="body1" paragraph sx={{ mb: 1, fontSize: { xs: '0.9rem', md: '1rem' } }}>
                  Hướng dẫn cách viết nội dung thu hút, phù hợp từng ngành nghề
                </Typography>
                <Typography variant="body1" paragraph sx={{ mb: 0, fontSize: { xs: '0.9rem', md: '1rem' } }}>
                  Seeding, review vào các group khách hàng mục tiêu
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* Benefit 3 - Cost Saving */}
          <Paper 
            elevation={2} 
            sx={{ 
              p: { xs: 2, md: 4 }, 
              mb: 3, 
              borderRadius: 3,
              background: 'linear-gradient(to right, #fce4ec, #ffffff)',
              border: '1px solid #f8bbd0',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: '0 12px 30px rgba(0, 0, 0, 0.08)',
              }
            }}
          >
            <Box 
              sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { xs: 'flex-start', md: 'center' },
                textAlign: { xs: 'left', md: 'left' },
                gap: 2
              }}
            >
              <Box sx={{ minWidth: { md: '200px' } }}>
                <Typography 
                  variant="h5" 
                  component="h3" 
                  color="primary" 
                  sx={{ 
                    fontWeight: 'bold',
                    mb: { xs: 1, md: 0 },
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: { xs: '1.1rem', md: '1.25rem' }
                  }}
                >
                  <Box 
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '45px',
                      height: '45px',
                      borderRadius: '50%',
                      background: 'linear-gradient(45deg, #c2185b, #f06292)',
                      color: 'white',
                      marginRight: '15px',
                      boxShadow: '0 4px 10px rgba(233, 30, 99, 0.3)',
                      flexShrink: 0
                    }}
                  >
                    <span role="img" aria-label="savings" style={{ fontSize: '22px' }}>💰</span>
                  </Box>
                  <span>Tiết kiệm chi phí</span>
                </Typography>
              </Box>
              <Box>
                <Typography variant="body1" paragraph sx={{ mb: 1, fontSize: { xs: '0.9rem', md: '1rem' } }}>
                  Bookdi đồng hành trong các chiến dịch deal hot, giảm giá, ngày làm đẹp
                </Typography>
                <Typography variant="body1" paragraph sx={{ mb: 0, fontSize: { xs: '0.9rem', md: '1rem' } }}>
                  Cửa hàng nhỏ vẫn có cơ hội được PR như thương hiệu lớn
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* Benefit 4 - Results */}
          <Paper 
            elevation={2} 
            sx={{ 
              p: { xs: 2, md: 4 }, 
              mb: 3, 
              borderRadius: 3,
              background: 'linear-gradient(to right, #f8bbd0, #ffffff)',
              border: '1px solid #f48fb1',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: '0 12px 30px rgba(0, 0, 0, 0.08)',
              }
            }}
          >
            <Box 
              sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { xs: 'flex-start', md: 'center' },
                textAlign: { xs: 'left', md: 'left' },
                gap: 2
              }}
            >
              <Box sx={{ minWidth: { md: '200px' } }}>
                <Typography 
                  variant="h5" 
                  component="h3" 
                  color="primary" 
                  sx={{ 
                    fontWeight: 'bold',
                    mb: { xs: 1, md: 0 },
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: { xs: '1.1rem', md: '1.25rem' }
                  }}
                >
                  <Box 
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '45px',
                      height: '45px',
                      borderRadius: '50%',
                      background: 'linear-gradient(45deg, #ad1457, #ec407a)',
                      color: 'white',
                      marginRight: '15px',
                      boxShadow: '0 4px 10px rgba(233, 30, 99, 0.3)',
                      flexShrink: 0
                    }}
                  >
                    <span role="img" aria-label="results" style={{ fontSize: '22px' }}>📈</span>
                  </Box>
                  <span>Kết quả mang lại</span>
                </Typography>
              </Box>
              <Box>
                <Typography variant="body1" paragraph sx={{ mb: 1, fontSize: { xs: '0.9rem', md: '1rem' } }}>
                  Tăng lượng khách hàng đến từ online
                </Typography>
                <Typography variant="body1" paragraph sx={{ mb: 1, fontSize: { xs: '0.9rem', md: '1rem' } }}>
                  Tăng lượt đặt lịch trực tiếp trên app
                </Typography>
                <Typography variant="body1" paragraph sx={{ mb: 0, fontSize: { xs: '0.9rem', md: '1rem' } }}>
                  Tăng doanh thu mà không cần thuê thêm nhân sự marketing
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>

        <Box 
          sx={{ 
            mt: 4, 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' }, 
            gap: 4, 
            justifyContent: 'center',
            animation: 'fadeIn 1s ease-out 0.9s',
            animationFillMode: 'both',
            '@keyframes fadeIn': {
              '0%': { opacity: 0, transform: 'translateY(20px)' },
              '100%': { opacity: 1, transform: 'translateY(0)' }
            }
          }}
        >
          <Card 
            elevation={3} 
            sx={{ 
              flex: '1 1 0',
              maxWidth: { xs: '100%', md: '45%' },
              borderRadius: { xs: 2, md: 4 },
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: '0 16px 30px rgba(0, 0, 0, 0.1)',
              },
            }}
          >
            <CardContent sx={{ p: { xs: 3, md: 4 }, display: 'flex', flexDirection: 'column', height: '100%' }}>
              <AppRegistration fontSize="large" sx={{ mb: 2, alignSelf: 'center', fontSize: '2.5rem', color: '#e91e63' }} />
              <Typography variant="h5" component="h2" gutterBottom fontWeight="600">
                Đăng ký đối tác
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4, flexGrow: 1 }}>
                Bạn là chủ doanh nghiệp? Đăng ký trở thành đối tác cung cấp dịch vụ với Bookdi ngay hôm nay. Mở rộng tầm với và tiếp cận nhiều khách hàng hơn.
              </Typography>
              <Button 
                component={Link} 
                to="/register" 
                variant="contained" 
                color="primary" 
                size="large"
                fullWidth
                sx={{ 
                  py: 1.5, 
                  fontWeight: 600,
                  borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                  bgcolor: '#e91e63',
                  '&:hover': {
                    bgcolor: '#c2185b'
                  }
                }}
              >
                Đăng ký ngay
              </Button>
            </CardContent>
          </Card>

          <Card 
            elevation={3} 
            sx={{ 
              flex: '1 1 0',
              maxWidth: { xs: '100%', md: '45%' },
              borderRadius: { xs: 2, md: 4 },
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: '0 16px 30px rgba(0, 0, 0, 0.1)',
              },
            }}
          >
            <CardContent sx={{ p: { xs: 3, md: 4 }, display: 'flex', flexDirection: 'column', height: '100%' }}>
              <AdminPanelSettings fontSize="large" sx={{ mb: 2, alignSelf: 'center', fontSize: '2.5rem', color: '#e91e63' }} />
              <Typography variant="h5" component="h2" gutterBottom fontWeight="600">
                Quản lý đối tác
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4, flexGrow: 1 }}>
                Dành cho Admin: Quản lý và xem xét các đối tác đã đăng ký trên nền tảng. Truy cập vào dashboard quản trị để xem thống kê và báo cáo.
              </Typography>
              <Button 
                component={Link} 
                to="/admin/partners" 
                variant="contained" 
                size="large"
                fullWidth
                sx={{ 
                  py: 1.5, 
                  fontWeight: 600,
                  borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                  bgcolor: '#9c27b0',
                  '&:hover': {
                    bgcolor: '#7b1fa2'
                  }
                }}
              >
                Đăng nhập Admin
              </Button>
            </CardContent>
          </Card>
        </Box>
        
        {/* Testimonials/Stats Section */}
        <Box 
          sx={{ 
            mt: 7, 
            mb: 4,
            animation: 'fadeIn 1s ease-out 1.1s',
            animationFillMode: 'both',
            '@keyframes fadeIn': {
              '0%': { opacity: 0 },
              '100%': { opacity: 1 }
            }
          }}
        >
          <Typography 
            variant="h4" 
            gutterBottom 
            sx={{ 
              fontWeight: 'bold',
              mb: 4,
              fontSize: { xs: '1.4rem', md: '2rem' },
              position: 'relative',
              display: 'inline-block',
              '&:after': {
                content: '""',
                position: 'absolute',
                width: '60px',
                height: '4px',
                background: 'linear-gradient(to right, #e91e63, #f8bbd0)',
                bottom: '-12px',
                left: 'calc(50% - 30px)',
                borderRadius: '2px'
              }
            }}
          >
            Đối tác tin tưởng Bookdi
          </Typography>
          
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-around', 
            flexWrap: 'wrap',
            gap: 3
          }}>
            {[
              { number: '500+', label: 'Đối tác', icon: '👥' },
              { number: '10,000+', label: 'Người dùng', icon: '👤' },
              { number: '50,000+', label: 'Lịch hẹn mỗi tháng', icon: '📅' }
            ].map((stat, index) => (
              <Box 
                key={index} 
                sx={{ 
                  textAlign: 'center', 
                  minWidth: { xs: '100px', md: '200px' },
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)'
                  }
                }}
              >
                <Typography variant="h2" sx={{ mb: 1, fontSize: { xs: '1.8rem', md: '2rem' } }}>
                  {stat.icon}
                </Typography>
                <Typography 
                  variant="h3" 
                  sx={{ 
                    fontWeight: 'bold',
                    fontSize: { xs: '1.6rem', md: '3rem' },
                    color: '#e91e63'
                  }}
                >
                  {stat.number}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {stat.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [loginOpen, setLoginOpen] = useState(false);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      setUser(currentUser);
    });

    return () => unsubscribe();
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      toast.success('Đăng xuất thành công');
    } catch (err) {
      console.error('Đăng xuất thất bại:', err);
      toast.error('Đăng xuất thất bại');
    }
  };

  return (
    <Router>
      <AppBar position="static" color="transparent" elevation={0} sx={{ backgroundColor: 'white', borderBottom: '1px solid #f8bbd0' }}>
        <Toolbar>
          <Typography 
            variant="h6" 
            component={Link} 
            to="/" 
            sx={{ 
              flexGrow: 1, 
              textDecoration: 'none', 
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              color: '#e91e63'
            }}
          >
            <Home sx={{ mr: 1, color: '#e91e63' }} /> Bookdi
          </Typography>
          <Button 
            color="inherit" 
            component={Link} 
            to="/register"
            sx={{ 
              color: '#e91e63',
              '&:hover': {
                bgcolor: 'rgba(233, 30, 99, 0.08)'
              }
            }}
          >
            Đăng ký đối tác
          </Button>
          
          {user ? (
            <>
              <Button 
                color="inherit" 
                component={Link} 
                to="/admin/partners"
                sx={{ 
                  color: '#e91e63',
                  '&:hover': {
                    bgcolor: 'rgba(233, 30, 99, 0.08)'
                  }
                }}
              >
                Admin
              </Button>
              <Button 
                color="inherit" 
                component={Link} 
                to="/admin/email-config" 
                startIcon={<Settings />}
                sx={{ 
                  color: '#e91e63',
                  '&:hover': {
                    bgcolor: 'rgba(233, 30, 99, 0.08)'
                  }
                }}
              >
                Cấu hình Email
              </Button>
              <Button 
                color="inherit" 
                component={Link} 
                to="/test/email" 
                startIcon={<Email />}
                sx={{ 
                  color: '#e91e63',
                  '&:hover': {
                    bgcolor: 'rgba(233, 30, 99, 0.08)'
                  }
                }}
              >
                Test Email
              </Button>
              <Button 
                color="error" 
                startIcon={<Logout />} 
                onClick={handleLogout}
              >
                Đăng xuất
              </Button>
            </>
          ) : (
            <Button 
              sx={{ 
                color: '#9c27b0',
                '&:hover': {
                  bgcolor: 'rgba(156, 39, 176, 0.08)'
                }
              }}
              startIcon={<Login />} 
              onClick={() => setLoginOpen(true)}
            >
              Đăng nhập
            </Button>
          )}
        </Toolbar>
      </AppBar>

      <Box sx={{ minHeight: 'calc(100vh - 64px)' }}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/register" element={<PartnerRegistration />} />
          <Route path="/admin/partners" element={<PartnerAdmin />} />
          <Route path="/admin/email-config" element={<TemporaryEmailConfigAdmin />} />
          <Route path="/test/email" element={<EmailTest />} />
        </Routes>
      </Box>

      <PartnerChatbot />

      <LoginDialog 
        open={loginOpen} 
        onClose={() => setLoginOpen(false)} 
        onLogin={() => {}}
      />

      <ToastContainer position="top-right" autoClose={5000} />
    </Router>
  );
}

export default App;
